import * as React from 'react';

import classNames from 'classnames';

import { cn } from '@wwe-portal/ui/lib/lib';

import type { OfferCardTypeEnum } from '@wwe-portal/entities/offer.type';

import { groupOfferByType, maxItemsByType } from './helper';

interface OfferGridProps<T extends { type: OfferCardTypeEnum }> {
  offers: T[];
  CardComponent: React.FC<T>;
  getKey: (item: T) => string;
  groupFn?: (data: T[]) => {
    type: OfferCardTypeEnum;
    id: string;
    children: T[];
  }[];
  className?: string;
  virtualIndex?: number;
}

function ItemGroup<T>({
  offers,
  CardComponent,
  className,
  getKey,
  virtualIndex,
}: Readonly<{
  offers: T[];
  getKey: (offer: T) => string;
  CardComponent: React.FC<T>;
  className: string;
  virtualIndex?: number;
}>) {
  return (
    <>
      {offers.map((offer) => (
        <div className={className} key={getKey(offer)}>
          <CardComponent
            key={getKey(offer)}
            {...offer}
            virtualIndex={virtualIndex}
          />
        </div>
      ))}
    </>
  );
}

export interface OfferGroupProps<Item> {
  groups: {
    type: OfferCardTypeEnum;
    id: string;
    children: Item[];
  }[];
  CardComponent: React.FC<Item>;
}

export function OfferGridGeneric<Item extends { type: OfferCardTypeEnum }>({
  offers,
  getKey,
  CardComponent,
  groupFn = groupOfferByType,
  className,
  virtualIndex,
}: Readonly<OfferGridProps<Item>>): React.JSX.Element {
  const groups = groupFn(offers);
  return (
    <div
      className={cn(
        '3xl:grid-cols-[repeat(18,_minmax(0,_1fr))] grid grid-cols-6 gap-[12px] xl:grid-cols-12',
        className
      )}
    >
      {groups.map((group) => {
        return (
          <ItemGroup
            CardComponent={CardComponent}
            className={classNames({
              'col-span-2': maxItemsByType[group.type] === 3,
              'col-span-3': maxItemsByType[group.type] === 2,
              'col-span-6': maxItemsByType[group.type] === 1,
            })}
            getKey={getKey}
            key={group.children.map((offer) => getKey(offer)).join('-')}
            offers={group.children}
            virtualIndex={virtualIndex}
          />
        );
      })}
    </div>
  );
}

export interface BannerCell {
  children: React.ReactNode;
  type: OfferCardTypeEnum;
  id: string;
}
